"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import DropCurationButton from "../DropCurationButton";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropInteractionParams } from "../drop.types";
import { DropLocation } from "../drop.types";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(95);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick
  } = t0;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  let t1;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t1 = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const identityProfile = t1;
  let t2;
  if ($[3] !== drop.metadata || $[4] !== drop.wave) {
    t2 = getParticipationVisibleMetadata({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const visibleMetadata = t2;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  let t3;
  if ($[6] !== router) {
    t3 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleNavigation = t3;
  let t4;
  if ($[8] !== hasTouch) {
    t4 = () => {
      if (!hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[8] = hasTouch;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleLongPress = t4;
  let t5;
  if ($[10] !== activePartIndex || $[11] !== drop || $[12] !== onReply) {
    t5 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[10] = activePartIndex;
    $[11] = drop;
    $[12] = onReply;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleOnReply = t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsSlideUp(false);
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleOnAddReaction = t6;
  const getDropLocationBackground = _temp;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t7 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t8 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t9;
  if ($[15] !== activePartIndex || $[16] !== drop || $[17] !== handleOnReply || $[18] !== isMobile || $[19] !== showReplyAndQuote) {
    t9 = !isMobile && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[15] = activePartIndex;
    $[16] = drop;
    $[17] = handleOnReply;
    $[18] = isMobile;
    $[19] = showReplyAndQuote;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== drop) {
    t10 = <WaveDropAuthorPfp drop={drop} />;
    $[21] = drop;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== drop.author.level) {
    t11 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[23] = drop.author.level;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== drop.author.handle || $[26] !== drop.author.primary_address || $[27] !== handleNavigation) {
    t12 = e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`);
    $[25] = drop.author.handle;
    $[26] = drop.author.primary_address;
    $[27] = handleNavigation;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = `/${drop.author.handle ?? drop.author.primary_address}`;
  const t14 = drop.author.handle ?? drop.author.primary_address;
  let t15;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={t12} href={t13} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t14}</Link></p>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== drop.created_at) {
    t17 = getTimeAgoShort(drop.created_at);
    $[34] = drop.created_at;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{t17}</p>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t11 || $[39] !== t15 || $[40] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-gap-x-2">{t11}{t15}{t16}{t18}</div>;
    $[38] = t11;
    $[39] = t15;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div>;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t19) {
    t21 = <div className="tw-flex tw-flex-col tw-gap-y-2">{t19}{t20}</div>;
    $[43] = t19;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== drop.wave || $[46] !== handleNavigation || $[47] !== showWaveInfo) {
    t22 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[45] = drop.wave;
    $[46] = handleNavigation;
    $[47] = showWaveInfo;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== activePartIndex || $[50] !== drop || $[51] !== handleLongPress || $[52] !== hasTouch || $[53] !== onDropContentClick || $[54] !== onQuoteClick) {
    t23 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} />;
    $[49] = activePartIndex;
    $[50] = drop;
    $[51] = handleLongPress;
    $[52] = hasTouch;
    $[53] = onDropContentClick;
    $[54] = onQuoteClick;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
    t24 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t21}{t22}{t23}</div>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t10 || $[61] !== t24) {
    t25 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t10}{t24}</div>;
    $[60] = t10;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== drop.id || $[64] !== identityProfile) {
    t26 = identityProfile && <div className="sm:tw-ml-[3.25rem]"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" /></div>;
    $[63] = drop.id;
    $[64] = identityProfile;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== visibleMetadata) {
    t27 = visibleMetadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={visibleMetadata} /></div>;
    $[66] = visibleMetadata;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  const t28 = drop.context_profile_context?.curatable ?? false;
  const t29 = drop.context_profile_context?.curated ?? false;
  let t30;
  if ($[68] !== drop.id || $[69] !== drop.wave.id || $[70] !== t28 || $[71] !== t29) {
    t30 = <DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={t28} isCurated={t29} />;
    $[68] = drop.id;
    $[69] = drop.wave.id;
    $[70] = t28;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== drop) {
    t31 = <WaveDropReactions drop={drop} />;
    $[73] = drop;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t30 || $[76] !== t31) {
    t32 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t30}{t31}</div>;
    $[75] = t30;
    $[76] = t31;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== drop || $[79] !== handleOnReply || $[80] !== isSlideUp || $[81] !== longPressTriggered || $[82] !== showReplyAndQuote) {
    t33 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[78] = drop;
    $[79] = handleOnReply;
    $[80] = isSlideUp;
    $[81] = longPressTriggered;
    $[82] = showReplyAndQuote;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t25 || $[85] !== t26 || $[86] !== t27 || $[87] !== t32 || $[88] !== t33 || $[89] !== t8 || $[90] !== t9) {
    t34 = <div className={t8}>{t9}{t25}{t26}{t27}{t32}{t33}</div>;
    $[84] = t25;
    $[85] = t26;
    $[86] = t27;
    $[87] = t32;
    $[88] = t33;
    $[89] = t8;
    $[90] = t9;
    $[91] = t34;
  } else {
    t34 = $[91];
  }
  let t35;
  if ($[92] !== t34 || $[93] !== t7) {
    t35 = <div className={t7}>{t34}</div>;
    $[92] = t34;
    $[93] = t7;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  return t35;
}
function _temp() {
  return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
}